import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../firebase';
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { user } = useAuth();

  // Listen for notifications
  useEffect(() => {
    if (!user) {
      console.log('🔔 NotificationContext: No user, skipping notification listener');
      return;
    }

    console.log('🔔 NotificationContext: Setting up listener for userId:', user.uid);

    // Query without orderBy to avoid index issues, we'll sort manually
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid)
    );

    const unsubscribe = onSnapshot(
      q, 
      (snapshot) => {
        const notificationsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Sort by createdAt descending (newest first)
        notificationsData.sort((a, b) => {
          const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
          const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
          return bTime - aTime;
        });
        
        console.log('🔔 NotificationContext: Received notifications:', notificationsData.length);
        console.log('🔔 NotificationContext: Notifications:', notificationsData.map(n => ({
          id: n.id,
          type: n.type,
          title: n.title,
          message: n.message,
          userId: n.userId,
          read: n.read,
          createdAt: n.createdAt
        })));
        
        setNotifications(notificationsData);
        setUnreadCount(notificationsData.filter(n => !n.read).length);
      },
      (error) => {
        console.error('🔔 NotificationContext: Error listening to notifications:', error);
      }
    );

    return unsubscribe;
  }, [user]);

  // Chat notifications are now handled by ChatService.sendMessage
  // This avoids duplicate notifications

  // Create a new notification
  const createNotification = async (notificationData) => {
    if (!user) return;

    try {
      await addDoc(collection(db, 'notifications'), {
        userId: user.uid,
        ...notificationData,
        createdAt: new Date(),
        read: false
      });
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.read);
      const updatePromises = unreadNotifications.map(notification =>
        updateDoc(doc(db, 'notifications', notification.id), { read: true })
      );
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Delete a notification
  const deleteNotification = async (notificationId) => {
    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Delete all notifications
  const deleteAllNotifications = async () => {
    try {
      const deletePromises = notifications.map(notification =>
        deleteDoc(doc(db, 'notifications', notification.id))
      );
      await Promise.all(deletePromises);
    } catch (error) {
      console.error('Error deleting all notifications:', error);
    }
  };

  // Create creator progress notification
  const createCreatorProgressNotification = (creator, action, campaignName, briefName) => {
    const getNotificationMessage = () => {
      switch (action) {
        case 'status_change':
          return `🎯 ${creator.creator} status changed to ${creator.statusText}`;
        case 'submitted':
          return `🎥 ${creator.creator} submitted their content`;
        case 'overdue':
          return `⚠️ ${creator.creator} is overdue`;
        case 'product_received':
          return `📦 ${creator.creator} received the product`;
        case 'filming_started':
          return `🎬 ${creator.creator} started filming`;
        case 'editing_started':
          return `✂️ ${creator.creator} started editing`;
        default:
          return `📝 ${creator.creator} updated their progress`;
      }
    };

    const getNotificationType = () => {
      switch (action) {
        case 'submitted':
          return 'success';
        case 'overdue':
          return 'warning';
        case 'status_change':
          return 'info';
        default:
          return 'info';
      }
    };

    createNotification({
      type: getNotificationType(),
      title: getNotificationMessage(),
      message: `${campaignName}${briefName ? ` • ${briefName}` : ''}`,
      creatorId: creator.id,
      creatorName: creator.creator,
      campaignName,
      briefName,
      action,
      status: creator.status,
      statusText: creator.statusText
    });
  };

  const value = {
    notifications,
    unreadCount,
    createNotification,
    createCreatorProgressNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
